import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ColDef,
  FilterChangedEvent,
  IRowNode,
  ITooltipParams,
  RowSelectionOptions,
  SelectionChangedEvent,
} from 'ag-grid-community'
import classNames from 'classnames'
import toast from 'react-hot-toast'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { subDays } from 'date-fns'
import { datadogRum } from '@datadog/browser-rum'
import humanizeDuration from 'humanize-duration'
import {
  Button,
  SearchInput,
  ProgressBar,
  tableTheme,
  Text,
  Switch,
} from 'src/components/ui'
import { OpcUaSyncTasks, TagDto } from 'src/services'
import { ErrorDisplay } from 'pages/app'
import { isDefined } from 'src/types'
import { useTagsBySiteId } from 'pages/site/tags/api'
import { useIsGatewayOnline } from '../../gateway'
import { useOpcSubscriptionNodes } from '../opc-ua-connection.api'
import {
  CustomTooltip,
  getHierarchyTreeConfig,
  SaveSubscriptionButton,
} from './opc-ua-hierarchy-tree'
import {
  getMainColumnDef,
  useChangeIndicators,
  useDebouncedFilterChange,
  useFuzzyFilter,
  useHandleSelected,
  useLocalStorageFileUrls,
  useNodeMap,
  useNodePaths,
  useNodeSubscriptions,
  useRowClassNames,
} from './opc-ua-hierarchy.utils'
import { useFilesQuery } from './opc-ua-hierarchy.api'
import { OpcUaNode, OpcUaNodeWithPath } from './opc-ua-hierarchy.types'
import { NoRecentlyDiscoveredNodesOverlay } from './opc-ua-hierarchy-tree/components/Overlay/Overlay'

type Props = {
  gatewayId: string
  connectionId: string
  siteId: string
  tasks: OpcUaSyncTasks
  subscriptionId: string
}

export enum Change {
  ADD = 'add',
  REMOVE = 'remove',
}

export function OpcUaHierarchyTree({
  connectionId,
  gatewayId,
  siteId,
  tasks,
  subscriptionId,
}: Props): JSX.Element {
  const [treeData, setTreeData] = useState<any>()
  const [isComputing, setIsComputing] = useState<boolean>(false)
  const { isOnline } = useIsGatewayOnline(siteId, gatewayId)
  const subscribedNodes = useOpcSubscriptionNodes(subscriptionId)
  const { getFileUrls } = useLocalStorageFileUrls(
    tasks.lastSuccessfulHierarchySyncTask?.id,
  )
  const storedFiles = getFileUrls()
  const tagsQuery = useTagsBySiteId(siteId)
  const filesQuery = useFilesQuery({
    files: [
      {
        id: 'nodes',
        url:
          storedFiles.nodes ||
          tasks.lastSuccessfulHierarchySyncTask?.arrowUriNodes ||
          '',
      },
      {
        id: 'references',
        url:
          storedFiles.references ||
          tasks.lastSuccessfulHierarchySyncTask?.arrowUriReferences ||
          '',
      },
    ],
  })

  const parseFiles = useCallback(
    async (nodes: Blob, references: Blob): Promise<void> => {
      setIsComputing(true)
      const startedAt = new Date()
      const nodesBuffer = await nodes.arrayBuffer()
      const referencesBuffer = await references.arrayBuffer()

      const worker = new Worker(
        new URL('./featherWorker.ts', import.meta.url),
        {
          type: 'module',
        },
      )

      worker.postMessage({ nodesBuffer, referencesBuffer })

      worker.onmessage = event => {
        const finishedAt = new Date()
        const duration = finishedAt.getTime() - startedAt.getTime()
        const nodesAnalytics = filesQuery.data?.analytics.get('nodes')
        const referencesAnalytics = filesQuery.data?.analytics.get('references')
        datadogRum.addAction('OPC-UA tree load', {
          nodeCount: event.data.nodes.length,
          referencesCount: event.data.referencesCount,
          fetching: {
            nodes: {
              timeToFetch: nodesAnalytics?.timeToFetch,
              size: nodesAnalytics?.size,
              cacheHit: nodesAnalytics?.cacheHit,
            },
            references: {
              timeToFetch: referencesAnalytics?.timeToFetch,
              size: referencesAnalytics?.size,
              cacheHit: referencesAnalytics?.cacheHit,
            },
          },
          timeToBuildTree: duration,
          siteId: siteId,
        })
        setTreeData(event.data.nodes)
        setIsComputing(false)
        worker.terminate()
      }

      worker.onerror = error => {
        console.error('Worker error:', error)
        worker.terminate()
      }
    },
    [filesQuery.data, siteId],
  )

  useEffect(() => {
    if (filesQuery.data) {
      const nodes = filesQuery.data.files.find(f => f.id === 'nodes')
      const references = filesQuery.data.files.find(f => f.id === 'references')
      if (nodes && references) {
        parseFiles(nodes.file, references.file)
      }
    }
  }, [filesQuery.data, parseFiles])

  // Fetch all pages of nodes
  useEffect(() => {
    if (subscribedNodes.hasNextPage && !subscribedNodes.isFetching) {
      subscribedNodes.fetchNextPage()
    }
  }, [subscribedNodes])

  const nodes = useMemo(
    () =>
      subscribedNodes.data?.pages
        .map(p => p.items)
        .flat()
        .filter(isDefined)
        .map(n => n.nodeId) ?? [],
    [subscribedNodes.data],
  )

  const subscribedNodesSet = useMemo(() => new Set(nodes), [nodes])

  if (
    filesQuery.isLoading ||
    subscribedNodes.isLoading ||
    tagsQuery.isLoading ||
    isComputing
  ) {
    const progressArray = Object.values(filesQuery.progress)
    const progressSum = progressArray.reduce((sum, currentValue) => {
      if (currentValue !== undefined) return sum + currentValue
      return sum
    }, 0)
    const progress = Math.min(progressSum / 2, 99)

    return (
      <div className="flex h-[150px] items-center justify-center rounded-2xs border border-solid border-border">
        <div className="flex w-full max-w-[250px] flex-col gap-xs text-center">
          <Text className="text-text-tertiary">
            {`Nodes loaded (${progress}%)`}
          </Text>
          <ProgressBar value={progress} />
        </div>
      </div>
    )
  }

  if (filesQuery.isError || subscribedNodes.isError || tagsQuery.isError) {
    const errorQuery = filesQuery.isError
      ? filesQuery
      : subscribedNodes.isError
      ? subscribedNodes
      : tagsQuery
    return (
      <ErrorDisplay
        error={errorQuery.error}
        message="Failed to fetch hierarchy nodes!"
        action={errorQuery.refetch}
      />
    )
  }

  if (treeData) {
    return (
      <HierarchyTree
        treeData={treeData}
        tags={tagsQuery.data}
        siteId={siteId}
        gatewayId={gatewayId}
        connectionId={connectionId}
        isGatewayOnline={isOnline}
        subscribedNodesSet={subscribedNodesSet}
        subscriptionId={subscriptionId}
      />
    )
  }

  return <></>
}

type TreeProps = Omit<Props, 'tasks'> & {
  isGatewayOnline: boolean
  treeData: Array<OpcUaNode>
  subscribedNodesSet: Set<string>
  tags: TagDto[]
}

function HierarchyTree({
  treeData,
  siteId,
  gatewayId,
  connectionId,
  isGatewayOnline,
  subscribedNodesSet,
  subscriptionId,
}: TreeProps): JSX.Element {
  const [recentlyAdded, setRecentlyAdded] = useState<boolean>(false)
  const recentDuration = useRef<string>()
  const [showNoRecentlyDiscoveredNodes, setShowNoRecentlyDiscoveredNodes] =
    useState(false)

  const { opcUaDataSubscriptions } = useFlags()
  const gridRef = useRef<AgGridReact>(null)

  const [isComputing, setIsComputing] = useState<boolean>(false)

  const [searchInput, setSearchInput] = useState<string>('')
  const searchInputRef = useRef<string>(searchInput)

  // Keeping track of the changes to the subscription
  const [subscriptionChanges, setSubscriptionChanges] =
    useState<Map<string, Change>>()
  const subscriptionChangesRef = useRef<Map<string, Change>>(new Map())

  // Keeping track of parents that have changed
  const [parentsChanged, setParentsChanged] =
    useState<Map<string, { added: number; removed: number }>>()
  const parentsChangedRef = useRef<
    Map<string, { added: number; removed: number }>
  >(new Map())

  // Custom hooks
  const handleRowClassNames = useRowClassNames(
    opcUaDataSubscriptions,
    subscriptionChangesRef,
    parentsChangedRef,
  )
  const handleSelected = useHandleSelected(
    opcUaDataSubscriptions,
    gridRef,
    subscribedNodesSet,
  )
  const getChangeIndicators = useChangeIndicators(
    subscriptionChangesRef,
    parentsChangedRef,
  )

  // Custom hooks for search
  const { doesFuzzyFilterPass, isFuzzyFilterPresent } =
    useFuzzyFilter(searchInputRef)
  const debouncedFilterChange = useDebouncedFilterChange(
    gridRef,
    searchInputRef,
  )

  useEffect(() => {
    debouncedFilterChange()
  }, [
    isFuzzyFilterPresent,
    doesFuzzyFilterPass,
    searchInput,
    debouncedFilterChange,
  ])

  // Convert the tree data to a map for easier access
  const nodeMap = useNodeMap(treeData)

  // Generate the tree structure
  const rootNode = treeData.find(node => node.extendedNodeId === 'i=85')
  const [convertedNodes, setConvertedNodes] = useState(
    useNodePaths(nodeMap, rootNode?.id ?? ''),
  )

  const getChildrenCount = useCallback(
    (nodes: OpcUaNodeWithPath[]) => {
      const children = new Map<string, number>()
      const subscribedChildren = new Map<string, number>()
      nodes.forEach(node => {
        node.path?.forEach((parent, index) => {
          if (index === (node.path?.length ?? 0) - 1) return
          children.set(parent, (children.get(parent) ?? 0) + 1)
          if (subscribedNodesSet.has(node.nodeId)) {
            subscribedChildren.set(
              parent,
              (subscribedChildren.get(parent) ?? 0) + 1,
            )
          }
        })
      })
      return {
        children,
        subscribedChildren,
      }
    },
    [subscribedNodesSet],
  )

  const convertedNodesArray = useMemo(() => {
    const gridNodes: OpcUaNodeWithPath[] = []
    gridRef.current?.api.forEachNodeAfterFilter(node => {
      gridNodes.push(node.data)
    })
    const counts = getChildrenCount(gridNodes)
    return [...convertedNodes.values()].map(v => {
      return {
        ...v,
        children: counts.children.get(v.id) ?? 0,
        subscribedChildren: counts.subscribedChildren.get(v.id) ?? 0,
      }
    })
  }, [convertedNodes, getChildrenCount])

  const { subscribedNodes, errorNodes } = useNodeSubscriptions(
    convertedNodesArray,
    subscribedNodesSet,
  )

  const childCount = useMemo(() => {
    const map = new Map<
      string,
      { children: number; subscribedChildren: number }
    >()
    convertedNodes.forEach(node => {
      node.path?.forEach(id => {
        const count = map.get(id) ?? { children: 0, subscribedChildren: 0 }
        count.children++
        if (subscribedNodes.has(id)) {
          count.subscribedChildren++
        }
        map.set(id, count)
      })
    })
    return map
  }, [convertedNodes, subscribedNodes])

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      minWidth: 300,
      initialFlex: 1,
      sortable: false,
      resizable: true,
    }
  }, [])

  const autoGroupColumnDef = useMemo<ColDef>(
    () => getMainColumnDef(errorNodes),
    [errorNodes],
  )

  // Update the refs when the state changes
  useEffect(() => {
    if (subscriptionChanges) {
      subscriptionChangesRef.current = subscriptionChanges
    }
    if (parentsChanged) {
      parentsChangedRef.current = parentsChanged
    }
    searchInputRef.current = searchInput
  }, [subscriptionChanges, parentsChanged, searchInput])

  // Search

  const handleSelectionChange = useCallback(
    (params: SelectionChangedEvent) => {
      if (params.source === 'gridInitializing') return
      setIsComputing(true)
      const selectedNodes = params.api.getSelectedRows()
      // Move the compute off the main thread
      const worker = new Worker(
        new URL('./selectionWorker.ts', import.meta.url),
        { type: 'module' },
      )
      worker.postMessage({
        subscriptionChanges: subscriptionChangesRef.current,
        parentChanges: parentsChangedRef.current,
        subscribedNodes: subscribedNodes,
        selectedNodes: selectedNodes,
        convertedNodes: convertedNodes,
      })

      worker.onmessage = e => {
        const { updatedSubscriptionChanges, updatedParentChanges } = e.data
        setSubscriptionChanges(updatedSubscriptionChanges)
        setParentsChanged(updatedParentChanges)
        setIsComputing(false)

        // Redraw the rows to show new styles for the new changes
        params.api.redrawRows()
        worker.terminate()
      }

      worker.onerror = error => {
        console.error('Worker error:', error)
        setIsComputing(false)
        worker.terminate()
      }
    },
    [convertedNodes, subscribedNodes],
  )

  const handleSelectableRow = useCallback((node: IRowNode): boolean => {
    return !!node.data?.isSubscribable
  }, [])

  // Count add changes
  const nodesAdded = useMemo(() => {
    return Array.from(subscriptionChanges?.values() ?? []).filter(
      change => change === Change.ADD,
    ).length
  }, [subscriptionChanges])

  // Count remove changes
  const nodesRemoved = useMemo(() => {
    return Array.from(subscriptionChanges?.values() ?? []).filter(
      change => change === Change.REMOVE,
    ).length
  }, [subscriptionChanges])

  const getNodesToChange = useCallback(() => {
    const nodesToAdd: string[] = []
    const nodesToRemove: string[] = []
    subscriptionChanges?.forEach((change, nodeId) => {
      if (change === Change.ADD) {
        nodesToAdd.push(nodeId)
      } else if (change === Change.REMOVE) {
        nodesToRemove.push(nodeId)
      }
    })
    return { nodesToAdd, nodesToRemove }
  }, [subscriptionChanges])

  const handleSuccess = useCallback(() => {
    // Update nodes that were subscribed and unsubscribed to avoid refetching all of the nodes
    const newMap = new Map(convertedNodes)
    setConvertedNodes(newMap)

    // reset changes
    setSubscriptionChanges(new Map())
    setParentsChanged(new Map())

    // close modal
    toast.success('Subscription hierarchy successfully saved', {
      position: 'top-right',
      duration: 10000,
    })

    // Redraw the rows to show new styles for the new changes
    gridRef.current?.api.redrawRows()
  }, [convertedNodes])

  const hnaldeDiscardStateChanges = useCallback(() => {
    setSubscriptionChanges(new Map())
    setParentsChanged(new Map())
    subscriptionChangesRef.current = new Map()
    parentsChangedRef.current = new Map()
  }, [setSubscriptionChanges, setParentsChanged])

  const handleDiscardChanges = useCallback(() => {
    if (!opcUaDataSubscriptions || !gridRef.current) return
    hnaldeDiscardStateChanges()
    const nodesToSelect: IRowNode[] = []
    const nodesToDeselect: IRowNode[] = []
    gridRef.current.api.forEachNode(node => {
      if (subscribedNodesSet.has(node.data.nodeId)) {
        nodesToSelect.push(node)
      } else {
        nodesToDeselect.push(node)
      }
    })
    gridRef.current.api.setNodesSelected({
      nodes: nodesToDeselect,
      newValue: false,
      source: 'gridInitializing',
    })
    gridRef.current.api.setNodesSelected({
      nodes: nodesToSelect,
      newValue: true,
      source: 'gridInitializing',
    })
    gridRef.current.api.redrawRows()
  }, [hnaldeDiscardStateChanges, opcUaDataSubscriptions, subscribedNodesSet])

  const getRowSelectionConfig = useCallback((): RowSelectionOptions => {
    return {
      mode: 'multiRow',
      groupSelects: 'self',
      headerCheckbox: false,
      isRowSelectable: handleSelectableRow,
    }
  }, [handleSelectableRow])

  const getSubscribedValue = useCallback((id: string, value: number) => {
    const parentsChanged = parentsChangedRef.current.get(id)
    if (parentsChanged) {
      return (value + parentsChanged.added - parentsChanged.removed).toString()
    }
    if (value === 0) return ''
    return value.toString()
  }, [])

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      const filteredNodes: OpcUaNodeWithPath[] = []
      let hasNodes = false
      event.api.forEachNodeAfterFilter(node => {
        hasNodes = true
        if (node.data) {
          filteredNodes.push(node.data)
        }
      })
      const childrenCount = getChildrenCount(filteredNodes)
      // Update the child count for all nodes
      event.api.forEachNodeAfterFilter(node => {
        node.setData({
          ...node.data,
          children: childrenCount.children.get(node.data.id) ?? 0,
          subscribedChildren:
            childrenCount.subscribedChildren.get(node.data.id) ?? 0,
        })
      })
      if (!hasNodes && recentlyAdded) {
        setShowNoRecentlyDiscoveredNodes(true)
      } else {
        setShowNoRecentlyDiscoveredNodes(false)
      }
      event.api.redrawRows()
    },
    [getChildrenCount, recentlyAdded],
  )

  const dataToDisplay = useMemo(() => {
    return convertedNodesArray.map(node => {
      return {
        ...node,
        childCount: childCount.get(node.id),
      }
    })
  }, [childCount, convertedNodesArray])

  const handleRecentlyAdded = useCallback((value: boolean) => {
    setRecentlyAdded(value)
    const currentFilter = gridRef.current?.api.getFilterModel()
    const updateFilter = { ...currentFilter }
    if (value) {
      updateFilter.addedAt = {
        dateFrom: subDays(new Date(), 7).toISOString(),
        dateTo: null,
        filterType: 'date',
        type: 'greaterThan',
      }
    } else {
      delete updateFilter?.addedAt
    }
    // using setTimeout to avoid the filter being applied before the state is updated
    // for better responsivness of the UI toggle
    if (updateFilter) {
      if (updateFilter.addedAt) {
        const fromDate = new Date(updateFilter.addedAt.dateFrom)
        const dateTo = updateFilter.addedAt.dateTo
          ? new Date(updateFilter.addedAt.dateTo)
          : new Date()
        const duration = dateTo.getTime() - fromDate.getTime()
        recentDuration.current = humanizeDuration(duration, {
          units: ['d', 'h'],
          round: true,
        })
      } else {
        recentDuration.current = undefined
      }
      setTimeout(() => gridRef.current?.api.setFilterModel(updateFilter), 10)
    }
  }, [])

  const isSubscribed = useCallback(
    (node: OpcUaNode): boolean => {
      return subscribedNodes.has(node.id)
    },
    [subscribedNodes],
  )

  const columnDefs = useMemo(() => {
    return getHierarchyTreeConfig({
      siteId,
      isSubscribed,
      subscriptionEnabled: opcUaDataSubscriptions,
      getChangeIndicators,
      getSubscribedValue,
    })
  }, [
    siteId,
    isSubscribed,
    opcUaDataSubscriptions,
    getChangeIndicators,
    getSubscribedValue,
  ])

  const memoedTree = useMemo(() => {
    return (
      <AgGridReact
        ref={gridRef}
        className="!h-[2000px]"
        theme={tableTheme.withParams({
          selectedRowBackgroundColor: 'transparent',
        })}
        enableCellTextSelection
        rowData={dataToDisplay}
        treeData={true}
        groupDefaultExpanded={1}
        loading={isComputing || showNoRecentlyDiscoveredNodes}
        // Using a custom loading overlay as a workaroud
        // to show message when there are no recently discovered nodes
        loadingOverlayComponent={
          showNoRecentlyDiscoveredNodes
            ? NoRecentlyDiscoveredNodesOverlay
            : undefined
        }
        loadingOverlayComponentParams={{
          duration: recentDuration.current,
        }}
        rowModelType="clientSide"
        suppressChangeDetection={true}
        rowSelection={
          opcUaDataSubscriptions ? getRowSelectionConfig() : undefined
        }
        onSelectionChanged={handleSelectionChange}
        onFilterChanged={onFilterChanged}
        onRowGroupOpened={event => {
          event.api.redrawRows({ rowNodes: [event.node] })
        }}
        getRowClass={handleRowClassNames}
        getDataPath={data => data.path}
        onFirstDataRendered={handleSelected}
        columnDefs={columnDefs}
        getRowId={data => data.data.id}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        isExternalFilterPresent={isFuzzyFilterPresent}
        doesExternalFilterPass={doesFuzzyFilterPass}
        tooltipShowDelay={0}
        selectionColumnDef={{
          tooltipComponent: CustomTooltip,
          tooltipValueGetter: (p: ITooltipParams) =>
            !p.data.isSubscribable ? 'Unsupported data type' : null,
        }}
      />
    )
  }, [
    dataToDisplay,
    isComputing,
    showNoRecentlyDiscoveredNodes,
    opcUaDataSubscriptions,
    getRowSelectionConfig,
    handleSelectionChange,
    onFilterChanged,
    handleRowClassNames,
    handleSelected,
    columnDefs,
    defaultColDef,
    autoGroupColumnDef,
    isFuzzyFilterPresent,
    doesFuzzyFilterPass,
  ])

  return (
    <div className="flex flex-col gap-s">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-s">
          <Text>
            <span className="font-500">
              Subscribed: {subscribedNodes.size - nodesRemoved + nodesAdded}
            </span>{' '}
            (Added: {nodesAdded}, Removed: {nodesRemoved})
          </Text>
        </div>
        <div className="flex items-center gap-xs">
          <Button
            disabled={subscriptionChanges?.size === 0}
            variant="secondary"
            title="Discard Changes"
            onClick={handleDiscardChanges}
          />
          <SaveSubscriptionButton
            gatewayId={gatewayId}
            siteId={siteId}
            connectionId={connectionId}
            subscriptionId={subscriptionId}
            getNodesToChange={getNodesToChange}
            onSuccess={handleSuccess}
            nodesAdded={nodesAdded}
            nodesRemoved={nodesRemoved}
            isDisabled={!isGatewayOnline}
          />
        </div>
      </div>
      <div className="size-full min-h-[600px] max-w-full" id="HierarchyTree">
        <div className="flex size-full flex-col rounded-2xs bg-background">
          <div className="flex items-center justify-between gap-xs rounded-t-2xs border border-b-0 border-solid border-border bg-background-hover px-s py-2xs">
            <div>
              <Text variant="description" bold>
                OPC-UA Hierarchy
              </Text>
            </div>
            <div className="flex items-center gap-s">
              <Switch
                label="Show Recently Discovered"
                labelVarinat="description"
                bold
                value={recentlyAdded}
                onChange={handleRecentlyAdded}
              />

              <SearchInput
                size="small"
                containerClassName="w-[200px]"
                value={searchInput}
                setValue={setSearchInput}
              />
            </div>
          </div>
          <div
            id="opcUaHierarchyTree"
            className={classNames(
              'w-full',
              !isComputing && 'hierarchy-tree-idle',
              recentlyAdded && 'opc-ua-recently-added',
            )}
          >
            {memoedTree}
          </div>
        </div>
      </div>
    </div>
  )
}
